import React, { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import apiService from '../api/apiService'
import { useAuth } from '../contexts/AuthContext'
import { Card } from './ui'

const UserProfile = () => {
  const { user } = useAuth()
  const [dbUser, setDbUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [stats, setStats] = useState({
    recipeCount: 0,
    avgRating: 0,
    favoritedCount: 0,
    totalRatings: 0,
    commentsCount: 0,
  })

  useEffect(() => {
    const fetchUserFromDb = async () => {
      if (!user?.email) return

      try {
        setLoading(true)
        
        // Fetch user data from API
        const userData = await apiService.getUserByEmail(user.email)
        setDbUser(userData)

        // Fetch user stats from the API endpoint
        if (userData && userData.id) {
          const statsData = await apiService.getUserStats(userData.id)
          setStats(statsData)
        }
      } catch (err) {
        setError('Failed to fetch user data from database')
        console.error(err)
      } finally {
        setLoading(false)
      }
    }

    fetchUserFromDb()
  }, [user])

  // Redirect to home page if user is not logged in
  if (!user) {
    return <Navigate to="/" replace />
  }

  return (
    <div className="mt-4">
      <Card>
        <Card.Header>
          <h2 className="text-xl font-semibold">Profile</h2>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <p className="text-center text-gray-500">Loading user data...</p>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          ) : dbUser ? (
            <div className="space-y-4">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 py-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-gray-200">
                  <div className="flex flex-col items-center justify-center px-2 pt-4 sm:pt-0">
                    <p className="text-sm font-medium text-gray-600">
                      Shared Recipes
                    </p>
                    <p className="text-2xl font-bold text-gray-800">
                      {stats.recipeCount}
                    </p>
                  </div>

                  <div className="flex flex-col items-center justify-center px-2 pt-4 sm:pt-0">
                    <p className="text-sm font-medium text-gray-600">
                      Average Rating
                    </p>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-800">
                        {stats.totalRatings > 0 ? (
                          <span
                            className={
                              stats.avgRating >= 4.5
                                ? 'text-green-600'
                                : stats.avgRating >= 4.0
                                  ? 'text-green-500'
                                  : stats.avgRating >= 3.5
                                    ? 'text-green-400'
                                    : stats.avgRating >= 3.0
                                      ? 'text-gray-500'
                                      : 'text-gray-400'
                            }
                          >
                            {stats.avgRating.toFixed(1)}
                          </span>
                        ) : (
                          <span className="text-gray-400">No ratings</span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col items-center justify-center px-2 pt-4 sm:pt-0">
                    <p className="text-sm font-medium text-gray-600">
                      Times Favorited
                    </p>
                    <p className="text-2xl font-bold text-yellow-500">
                      {stats.favoritedCount}
                    </p>
                  </div>

                  <div className="flex flex-col items-center justify-center px-2 pt-4 sm:pt-0">
                    <p className="text-sm font-medium text-gray-600">
                      Comments Made
                    </p>
                    <p className="text-2xl font-bold text-blue-500">
                      {stats.commentsCount}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    {dbUser.image_url && (
                      <img
                        src={dbUser.image_url}
                        alt={`${dbUser.first_name} ${dbUser.last_name}`}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    )}
                    <div className="text-left">
                      <h3 className="text-base font-medium">
                        {dbUser.first_name} {dbUser.last_name}
                      </h3>
                      <p className="text-xs text-gray-600">{dbUser.email}</p>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="text-xs text-gray-600">Forkasting Since</p>
                    <p className="text-lg text-gray-800">
                      {dbUser.created_at
                        ? new Date(dbUser.created_at).toLocaleDateString(
                            undefined,
                            { year: 'numeric', month: 'long' }
                          )
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-center text-yellow-600">
              User data not found in database. This could happen if there was an
              error storing your data.
            </p>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

export default UserProfile
